import type { ReactElement } from "react";
import { useMemo, useRef, useState } from "react";

import uniq from "lodash/uniq";
import { CSSTransition, SwitchTransition } from "react-transition-group";

import { objectKeys } from "app/helpers/objects";
import type { OnboardingDescriptionDto } from "app/pages/sales-intelligence/sub-modules/onboarding/types/OnboardingDescription.dto";
import type {
    OnboardingWizardActiveFocusItems,
    OnboardingWizardAnswer,
} from "app/pages/sales-intelligence/sub-modules/onboarding/types/OnboardingWizardAnswer.dto";
import { WizardStep } from "app/pages/sales-intelligence/sub-modules/onboarding/types/WizardStepType";
import { StyledAnimationWrapper } from "app/pages/sales-intelligence/sub-modules/onboarding/wizard/containers/styles";
import onboardingWizardTrackingService from "app/pages/sales-intelligence/sub-modules/onboarding/wizard/services/tracking/trackingService";

import WizardFocusQuestionsContainer from "./WizardFocusQuestionsContainer";
import WizardGoalQuestionsContainer from "./WizardGoalQuestionsContainer";
import type {
    OnboardingFocusQuestionType,
    OnboardingGoalQuestionType,
} from "app/pages/sales-intelligence/sub-modules/onboarding/types/enums";

interface Props {
    currentStep: WizardStep;
    isLoading: boolean;
    data?: OnboardingDescriptionDto;
    onSubmit: (answer: OnboardingWizardAnswer) => void;
    onClose: () => void;
    onStepChange: (step: WizardStep) => void;
}

const WizardContentContainer = ({
    data,
    currentStep,
    isLoading,
    onSubmit,
    onClose,
    onStepChange,
}: Props): ReactElement | null => {
    const [activeFocusItems, setActiveFocusItems] = useState<OnboardingWizardActiveFocusItems>(
        {} as OnboardingWizardActiveFocusItems,
    );
    const [activeGoal, setActiveGoal] = useState<OnboardingGoalQuestionType | null>(null);

    const availableGoals = useMemo((): OnboardingGoalQuestionType[] => {
        if (!data) {return [];}

        const goals = data.reduce((acum, { userGoal, actionPoints }) => {
            if (!activeFocusItems[userGoal]) {
                return acum;
            }

            return [...acum, ...actionPoints];
        }, []);

        return uniq(goals);
    }, [data, activeFocusItems]);

    const isFocusQuestionsValid = availableGoals.length > 0;
    const isGoalQuestionsValid = activeGoal !== null;

    const handleNext = (): void => {
        if (!isFocusQuestionsValid) {
            return;
        }

        onboardingWizardTrackingService.trackClickNextOnFocusQuestions(
            objectKeys(activeFocusItems).filter((key) => activeFocusItems[key]),
        );

        onStepChange(WizardStep.Goals);
    };

    const handleSubmit = (): void => {
        if (!isFocusQuestionsValid || !activeGoal) {
            return;
        }

        onboardingWizardTrackingService.trackClickNextOnGoalQuestions(activeGoal);

        onSubmit({
            SelectedGoals: objectKeys(activeFocusItems),
            StartingAction: activeGoal,
        });
    };

    const handleFocusCardClick = (focusCard: OnboardingFocusQuestionType) => {
        onboardingWizardTrackingService.trackClickOnFocusOption(focusCard);

        setActiveFocusItems((prevState) => ({
            ...prevState,
            [focusCard]: !prevState[focusCard],
        }));
    };

    const handleGoalCardClick = (goalCard: OnboardingGoalQuestionType) => {
        onboardingWizardTrackingService.trackClickOnGoalOption(goalCard);

        setActiveGoal(goalCard === activeGoal ? null : goalCard);
    };

    const handleBack = (): void => {
        onboardingWizardTrackingService.trackClickBack();

        onStepChange(WizardStep.Focus);
    };

    const handleLinkClick = (): void => {
        onboardingWizardTrackingService.trackClickOnSkip();

        onClose();
    };

    const nodeRef = useRef(null);

    return (
        <SwitchTransition>
            <CSSTransition
                key={currentStep === WizardStep.Focus ? "focus" : "goal"}
                in
                mountOnEnter={false}
                timeout={500}
                classNames="item"
                nodeRef={nodeRef}
            >
                <StyledAnimationWrapper ref={nodeRef}>
                    {currentStep === WizardStep.Focus || !isFocusQuestionsValid ? (
                        <WizardFocusQuestionsContainer
                            isValid={isFocusQuestionsValid}
                            isLoading={isLoading}
                            cardItems={data || []}
                            activeCards={activeFocusItems}
                            onCardClick={handleFocusCardClick}
                            onNext={handleNext}
                        />
                    ) : (
                        <WizardGoalQuestionsContainer
                            isValid={isGoalQuestionsValid}
                            isLoading={isLoading}
                            cardItems={availableGoals}
                            activeCard={activeGoal}
                            onCardClick={handleGoalCardClick}
                            onNext={handleSubmit}
                            onBack={handleBack}
                            onClose={handleLinkClick}
                        />
                    )}
                </StyledAnimationWrapper>
            </CSSTransition>
        </SwitchTransition>
    );
};

export default WizardContentContainer;
